// Notifications frontend helpers — parent prefs + admin log + send wrappers.

import axios from "axios";
import { supabase, isSupabaseConfigured } from "./supabase";

const API = process.env.REACT_APP_BACKEND_URL;

function ensureLive() {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
}

async function authHeader() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("Not signed in");
  return { Authorization: `Bearer ${token}` };
}

// ---------------------------------------------------------------------------
// Parent preferences
// ---------------------------------------------------------------------------

export const PREF_KEYS = [
  { key: "daily_reports",       label: "Daily reports",       desc: "One email per child when a daily report is logged." },
  { key: "incidents",           label: "Incident reports",    desc: "Emailed whenever a staff member files an incident. Always on for safety." },
  { key: "invoices",            label: "New invoices",        desc: "When a new tuition or other invoice is issued." },
  { key: "autopay_failures",    label: "Autopay failures",    desc: "When an automatic payment doesn't go through. Always on for billing." },
  { key: "event_confirmations", label: "Event confirmations", desc: "A brief confirmation email when you RSVP 'going' to an event." },
  { key: "event_reminders",     label: "Event reminders",     desc: "A friendly nudge ~48 hours before an event you haven't RSVP'd to yet." },
  { key: "weekly_photo_recap",  label: "Weekly photo recap",  desc: "A warm Sunday digest with up to 3 photos per child from the past week." },
  { key: "new_message_emails",  label: "New messages (email)", desc: "A short email when a teacher or the center messages you (throttled so it's never spammy)." },
  { key: "new_message_push",    label: "New messages (push)",  desc: "A push notification to this device when a teacher or the center messages you." },
];

export const PREF_ALWAYS_ON = new Set(["incidents", "autopay_failures"]);

export async function getPreferences(profileId) {
  ensureLive();
  const { data, error } = await supabase
    .from("notification_preferences")
    .select(
      "profile_id, daily_reports, incidents, invoices, autopay_failures, event_confirmations, event_reminders, weekly_photo_recap, new_message_emails, new_message_push, updated_at",
    )
    .eq("profile_id", profileId)
    .maybeSingle();
  if (error) throw error;
  return data || {
    profile_id: profileId,
    daily_reports: true,
    incidents: true,
    invoices: true,
    autopay_failures: true,
    event_confirmations: true,
    event_reminders: true,
    weekly_photo_recap: true,
    new_message_emails: true,
    new_message_push: true,
  };
}

export async function upsertPreferences(profileId, patch) {
  ensureLive();
  const { data, error } = await supabase
    .from("notification_preferences")
    .upsert(
      { profile_id: profileId, ...patch },
      { onConflict: "profile_id" },
    )
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Admin log
// ---------------------------------------------------------------------------

export async function listNotificationLog({ limit = 20 } = {}) {
  ensureLive();
  const { data, error } = await supabase
    .from("notifications_log")
    .select("id, kind, to_email, subject, status, resend_id, error, created_at, metadata")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

// ---------------------------------------------------------------------------
// Send wrappers — call the FastAPI endpoints. All are fire-and-log; we
// swallow failures on the UI side because the backend already logs them
// (the notification log panel is the source of truth for delivery).
// ---------------------------------------------------------------------------

async function fire(path, body) {
  try {
    const headers = await authHeader();
    const res = await axios.post(`${API}/api/notifications${path}`, body, { headers });
    return res.data;
  } catch (err) {
    // Return a shape the caller can inspect, but don't throw — we never
    // want a notification failure to block a primary action like
    // issuing an invoice.
    return { status: "failed", reason: err?.response?.data?.detail || err.message };
  }
}

export function notifyDailyReport(payload) { return fire("/send/daily-report", payload); }
export function notifyIncident(payload)    { return fire("/send/incident",     payload); }
export function notifyInvoiceIssued(payload) { return fire("/send/invoice-issued", payload); }
export function notifyAutopayFailure(payload) { return fire("/send/autopay-failure", payload); }
export function notifyEventRsvpConfirmed(payload) { return fire("/send/event-rsvp-confirmed", payload); }
export function notifyNewMessage(messageId) {
  if (typeof messageId !== "string" || !messageId.trim()) {
    return Promise.resolve({ status: "failed", reason: "message_id_required" });
  }
  // This is deliberately a scalar API. Recipient, sender, organization,
  // preview, mention, and preference fields belong to the server-derived
  // message snapshot and must never be constructed by a browser caller.
  return fire("/send/new-message", { message_id: messageId });
}

// ---------------------------------------------------------------------------
// Status metadata
// ---------------------------------------------------------------------------

export const NOTIFICATION_STATUS_META = {
  sent:    { label: "Sent",    tone: "border-primary/30 bg-primary/10 text-primary-hover" },
  failed:  { label: "Failed",  tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#C6851E]"  },
  skipped: { label: "Skipped", tone: "border-neutral-200 bg-neutral-100 text-neutral-500" },
};

export const NOTIFICATION_KIND_LABELS = {
  daily_report:         "Daily report",
  incident:             "Incident",
  invoice_issued:       "Invoice issued",
  autopay_failure:      "Autopay failure",
  event_rsvp_confirmed: "Event RSVP confirmed",
  event_reminder:       "Event reminder",
  weekly_photo_recap:   "Weekly photo recap",
  new_message:          "New message",
  new_message_push:     "New message (push)",
  native_push:          "Native push",
  enrollment_inquiry:   "Enrollment inquiry",
};
